import React from 'react';
import { Card } from './ui/card';
import { MapPin, Building2, CheckCircle } from 'lucide-react';
import { locations } from '../data/mock';

const Locations = () => {
  return (
    <section id="lieux" className="py-16 md:py-24 bg-[#F8F8F5]">
      <div className="max-w-[1600px] mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#4A6B96]/5 px-4 py-2 rounded-full mb-6">
            <MapPin className="text-[#4A6B96]" size={20} />
            <span className="text-sm font-medium text-[#4A6B96]">Nos Lieux</span>
          </div>
          
          <h2 className="text-3xl md:text-5xl font-bold text-[#4A6B96] mb-6">
            Centre d'affaires implanté dans l'Ouest Parisien
          </h2>
          
          <p className="text-base md:text-xl text-gray-600">
            Découvrez nos centres d'affaires premium à Versailles, Jouy-en-Josas et Viroflay, 
            idéalement situés en centre-ville, accessibles et connectés. Notre manoir en Normandie accueille vos séminaires.
          </p>
        </div>

        {/* Locations Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {locations.map((location, index) => (
            <Card 
              key={location.id}
              className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="relative h-72 overflow-hidden bg-[#4A6B96]">
                <img
                  src={location.image}
                  alt={location.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  style={location.imagePosition ? { objectPosition: location.imagePosition } : undefined}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                
                {/* Status Badge */}
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${
                  location.status === 'Ouvert' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-[#FFFFFF] text-[#4A6B96]'
                }`}>
                  {location.status}
                </div>

                {/* Location Name Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">
                    {location.name}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start space-x-2 mb-4">
                  <MapPin className="text-[#4A6B96] flex-shrink-0 mt-1" size={18} />
                  <p className="text-gray-600 text-sm whitespace-pre-line">
                    {location.address}
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Building2 className="text-[#4A6B96] flex-shrink-0" size={18} />
                  <p className="text-gray-600 text-sm whitespace-pre-line">
                    {location.spaces}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Map Section */}
        <div className="premium-blue rounded-2xl p-12 text-white relative overflow-hidden">
          {/* Decorative gold accent */}
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-[#FFFFFF]/10 blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#7B9BC0]/15 blur-3xl pointer-events-none"></div>
          <div className="relative grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">
                Idéalement Situés
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-white font-bold flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">
                    <span className="font-semibold text-white">Centre-ville</span> - À proximité des commerces, restaurants et services
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-white font-bold flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">
                    <span className="font-semibold text-white">Transports</span> - Accès direct métro, tram, bus et gare
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-white font-bold flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">
                    <span className="font-semibold text-white">Parking</span> - Places de stationnement disponibles à proximité
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-white font-bold flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">
                    <span className="font-semibold text-white">Accessibilité</span> - Bâtiments modernes et accessibles PMR
                  </p>
                </div>
              </div>
            </div>

            <div className="h-80 bg-gray-200 rounded-xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_bureaux-prestige/artifacts/9rabweez_ChatGPT%20Image%205%20mai%202026%2C%2011_17_17.png"
                alt="Idéalement situés - Bâtiments haussmanniens de l'Ouest parisien"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* SEO content: Description longue par lieu - utile pour Google */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8 text-gray-700">
          <div>
            <h3 className="text-lg font-bold text-[#4A6B96] mb-3">Centre d'affaires Versailles</h3>
            <p className="text-sm leading-relaxed">
              Situé au 16 Boulevard de la Reine, notre centre d'affaires de Versailles propose <strong>19 bureaux privatifs</strong> et une salle de réunion premium. À deux pas du Château de Versailles et de la gare Versailles-Rive-Droite, il offre une adresse prestigieuse pour votre activité, avec un accès direct à Paris en 30 minutes.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#4A6B96] mb-3">Centre d'affaires Jouy-en-Josas</h3>
            <p className="text-sm leading-relaxed">
              Notre centre d'affaires de Jouy-en-Josas est idéalement placé au 5-5bis Rue du Petit Robinson, à proximité immédiate d'<strong>HEC Paris</strong>. Un cadre verdoyant et calme pour recevoir vos clients, avec bureaux privatifs et coworking design, dans un cadre boisé propice à la concentration.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#4A6B96] mb-3">Centre d'affaires Viroflay</h3>
            <p className="text-sm leading-relaxed">
              Implanté au 7 Rue des Vignes à Viroflay, notre centre propose <strong>6 bureaux privatifs</strong> dans un espace design et convivial. Accessibilité optimale grâce à la gare de Viroflay-Rive-Droite (ligne L) et à la proximité de Chaville, Vélizy et Meudon.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-bold text-[#4A6B96] mb-3">Manoir séminaire en Normandie</h3>
            <p className="text-sm leading-relaxed">
              Notre <strong>manoir au Neubourg</strong> (Normandie) accueille vos <strong>séminaires d'entreprise, réceptions et retraites d'équipe</strong>. À 1h30 de Paris, cet écrin d'exception combine confort haut de gamme, salles de réunion et espaces de convivialité pour marquer les esprits.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Locations;
